import React, { useState, useEffect } from 'react';
import { obtenerHistorialPagos } from '../../services/FinanzasService';
import { SlidersHorizontal, Loader, ExternalLink } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const Button = ({ children, onClick, className = '', variant = 'primary', size = 'default' }) => {
    const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
    
    const variantStyles = {
        primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
        green: 'bg-emerald-500 text-white hover:bg-emerald-600'
    };

    const sizeStyles = {
        default: 'px-4 py-2 text-sm',
        sm: 'px-3 py-1.5 text-xs'
    };

    return (
        <button
            onClick={onClick}
            className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        >
            {children}
        </button>
    );
};

const Ingresos = () => {
  const [historialPagos, setHistorialPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('fecha');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const mesesNombres = {
    1: 'Enero',
    2: 'Febrero', 
    3: 'Marzo',
    4: 'Abril',
    5: 'Mayo',
    6: 'Junio',
    7: 'Julio',
    8: 'Agosto', 
    9: 'Septiembre',
    10: 'Octubre',
    11: 'Noviembre',
    12: 'Diciembre'
  };

  useEffect(() => {
    const cargarHistorial = async () => {
      try {
        const response = await obtenerHistorialPagos();
        if (response?.historialPagos) {
          const historialOrdenado = response.historialPagos.sort((a, b) => 
            new Date(b.fecha_pago) - new Date(a.fecha_pago)
          );
          setHistorialPagos(historialOrdenado);
        } else {
          setHistorialPagos([]);
        }
      } catch (error) {
        console.error("Error al cargar historial:", error);
        showErrorToast('Error al obtener datos de la base de datos');
        setHistorialPagos([]);
      } finally {
        setLoading(false);
      }
    };
    cargarHistorial();
  }, []);

  const historialFiltrado = historialPagos.filter(pago => {
    return (!filtroMes || pago.mes_pagado.toString() === filtroMes) &&
           (!filtroAnio || pago.anio_pagado.toString() === filtroAnio) &&
           (!filtroTipo || pago.tipo_pago === filtroTipo);
  });

  const totalIngresos = historialFiltrado.reduce((sum, pago) => 
    sum + parseFloat(pago.monto_pagado), 0
  );

  const mesesUnicos = [...new Set(historialPagos.map(pago => pago.mes_pagado))].sort();
  const aniosUnicos = [...new Set(historialPagos.map(pago => pago.anio_pagado))].sort();
  const tiposPago = [...new Set(historialPagos.map(pago => pago.tipo_pago))];

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historialFiltrado.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(historialFiltrado.length / itemsPerPage);

  const limpiarFiltros = () => {
    setFiltroMes('');
    setFiltroAnio('');
    setFiltroTipo('');
    setCurrentPage(1);
  };

  const renderFiltroActivo = () => {
    switch(filtroActivo) {
      case 'fecha':
        return (
          <div className="flex gap-2">
            <select 
              value={filtroMes}
              onChange={(e) => setFiltroMes(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Todos los meses</option>
              {mesesUnicos.map(mes => (
                <option key={mes} value={mes}>{mesesNombres[mes]}</option>
              ))}
            </select>

            <select
              value={filtroAnio}
              onChange={(e) => setFiltroAnio(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Todos los años</option>
              {aniosUnicos.map(anio => (
                <option key={anio} value={anio}>{anio}</option>
              ))}
            </select>
          </div>
        );
      case 'tipo':
        return (
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">Todos los tipos</option>
            {tiposPago.map(tipo => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-6">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="mt-16"
        style={{ zIndex: 9999 }}
      />
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                  Ingresos
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Gestiona los ingresos del centro
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              {/* Filtros en móvil */}
              <div className="block sm:hidden">
                <select
                  onChange={(e) => setFiltroActivo(e.target.value)}
                  value={filtroActivo}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm"
                >
                  <option value="fecha">Fecha</option>
                  <option value="tipo">Tipo</option>
                  <option value="busqueda">Buscar</option>
                </select>
              </div>

              {/* Filtros en desktop */}
              <div className="hidden sm:flex gap-2">
                <button
                  onClick={() => setFiltroActivo('fecha')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroActivo === 'fecha'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Fecha
                </button>
                <button
                  onClick={() => setFiltroActivo('tipo')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroActivo === 'tipo'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Tipo
                </button>

              </div>

              {filtroActivo && (
                <div className="mt-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      {renderFiltroActivo()}
                    </div>
                    {(filtroMes || filtroAnio || filtroTipo) && (
                      <button
                        onClick={limpiarFiltros}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 whitespace-nowrap"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="text-lg sm:text-xl font-semibold text-green-600">
              Total: ${totalIngresos.toFixed(2)}
            </div>
          </div>

          {/* Vista móvil */}
          <div className="block sm:hidden">
            {loading ? (
              <div className="p-4 flex justify-center">
                <Loader className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {currentItems.map((pago) => (
                  <div key={pago.id_pago} className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="text-xs text-gray-500">
                          {new Date(pago.fecha_pago).toLocaleDateString()}
                        </div>
                        <div className="font-medium text-green-600">
                          ${parseFloat(pago.monto_pagado).toFixed(2)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {mesesNombres[pago.mes_pagado]}/{pago.anio_pagado}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {pago.tipo_pago}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      {pago.tipo_pago === 'Transferencia' && (
                        <a 
                          href={pago.comprobante} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 text-xs"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>Comprobante</span>
                        </a>
                      )}
                      {pago.comprobante_url && (
                        <a 
                          href={pago.comprobante_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 text-xs"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>PDF</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vista desktop */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes/Año</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comprobante</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4">
                      <div className="flex justify-center">
                        <Loader className="w-6 h-6 animate-spin text-indigo-500" />
                      </div>
                    </td>
                  </tr>
                ) : currentItems.map((pago) => (
                  <tr key={pago.id_pago} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(pago.fecha_pago).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      ${parseFloat(pago.monto_pagado).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pago.tipo_pago}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {mesesNombres[pago.mes_pagado]}/{pago.anio_pagado}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {pago.tipo_pago === 'Transferencia' ? (
                        <a 
                          href={pago.comprobante} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver Comprobante
                        </a>
                      ) : pago.comprobante?.startsWith('pi_') ? 'Pago por tarjeta' : 
                         pago.comprobante}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {pago.comprobante_url && (
                        <a 
                          href={pago.comprobante_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex justify-between sm:hidden">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                variant="ghost"
                size="sm"
                  className="text-xs"
              >
                Anterior
              </Button>
                <span className="text-xs text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                variant="ghost"
                size="sm"
                  className="text-xs"
              >
                Siguiente
              </Button>
            </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, historialFiltrado.length)}
                  </span>{' '}
                  de <span className="font-medium">{historialFiltrado.length}</span> resultados
                </p>
              </div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`relative inline-flex items-center px-3 sm:px-4 py-2 border text-xs sm:text-sm font-medium
                        ${currentPage === i + 1 
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }
                        ${i === 0 ? 'rounded-l-md' : ''}
                        ${i === totalPages - 1 ? 'rounded-r-md' : ''}
                      `}
                    >
                      {i + 1}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ingresos;
